/**
 * Protected Route Component
 *
 * A route wrapper component that handles authentication and authorization.
 * Redirects unauthenticated users to login and unauthorized users to access denied page.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * ProtectedRoute - Wraps routes that require authentication/authorization
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if access is granted
 * @param {string|null} [props.requiredPermission=null] - Specific permission required to access route
 * @param {number|null} [props.requiredRoleId=null] - Specific role ID required to access route (e.g., 6 for super admin)
 * @returns {JSX.Element} Protected content or redirect component
 */
const ProtectedRoute = ({ children, requiredPermission = null, requiredRoleId = null }) => {
  const { user, userLoading, isLoading, isLoggingOut } = useAuth();

  // Dev bypass: add ?bypass=true to URL to skip permission checks in development
  const urlParams = new URLSearchParams(window.location.search);
  const devBypass = process.env.NODE_ENV === 'development' && urlParams.get('bypass') === 'true';

  // Show loading state while user data is being fetched OR during logout transition
  // This prevents the flash of login screen when logging out
  if (userLoading || isLoading || isLoggingOut) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // If no user data is available after loading, something went wrong
  if (!user) {
    console.error('ProtectedRoute: No user data available after loading');
    return <Navigate to="/login" replace />;
  }

  // Dev bypass skips all permission checks
  if (devBypass) {
    return children;
  }

  // Check for required role ID first (more restrictive)
  // Also check for emulated role in development
  const emulatedRoleId = localStorage.getItem('emulated_role_id');
  const effectiveRoleId = emulatedRoleId ? parseInt(emulatedRoleId) : (user.role_id || user.full_role?.id);

  if (requiredRoleId !== null && effectiveRoleId !== requiredRoleId) {
    console.log(`ProtectedRoute: User role_id ${effectiveRoleId} does not match required role_id ${requiredRoleId}`);
    return <Navigate to="/access-denied" replace />;
  }

  // If no specific permission is required, just check if logged in
  if (!requiredPermission) {
    return children;
  }

  // Check if user has full_role before accessing it
  if (!user.full_role) {
    console.error('ProtectedRoute: User object missing full_role property', user);
    return <Navigate to="/access-denied" replace />;
  }

  const has_permission = () => (user.full_role[requiredPermission] || user.full_role['all'])

  // Check if user has the required permission
  if (has_permission()) {
    return children;
  }

  // If user doesn't have permission, redirect to access denied
  return <Navigate to="/access-denied" replace />;
};

export default ProtectedRoute;